import { clamp } from "es-toolkit";
import { Vector3 } from "three";
import { GUIDED_NAVIGATION_PARAMS as P } from "./guided-navigation-params";

/** Number of floats per trajectory segment: x0, y0, z0, x1, y1, z1 */
export const SEGMENT_STRIDE = 6;

/** Squared translation reduction above which a movement is considered constrained */
const CONSTRAINT_EPSILON_SQ = 1e-10;

/** Lengths below this are considered zero */
const LENGTH_EPSILON = 1e-6;

/** Iterations of the golden-section search of the tunnel point closest to an outside position */
const PROJECTION_ITERATIONS = 32;

const GOLDEN_RATIO = (Math.sqrt(5) - 1) / 2;

const MOVE = new Vector3();
const CENTER = new Vector3();

/**
 * Position relative to the trajectory tunnels, made of the points within P.corridorRadius horizontally
 * and P.tunnelHalfHeight vertically of a trajectory point
 */
export type TunnelPoint = {
  /** Whether any trajectory segment exists */
  found: boolean;
  /** Distance to the tunnel boundary, positive inside and negative outside */
  depth: number;
  /** Closest point of the tunnel boundary */
  boundaryPoint: Vector3;
  /** Outward normal of the tunnel boundary at the boundary point */
  normal: Vector3;
  /** Direction of the trajectory segment of the boundary point */
  tangent: Vector3;
};

/** Result of the last raycast on the Gaussian Splats */
export type SplatHit = {
  /** Whether a splat was hit */
  valid: boolean;
  /** Distance of the closest hit splat from the ray origin */
  distance: number;
  /** Ray origin */
  origin: Vector3;
  /** Normalized ray direction */
  direction: Vector3;
};

/** Information about the proximity of the camera to the splats hit by a ray, used to render the guides */
export type SplatGuidance = {
  /** From 0 (far) to 1 (closest allowed distance) the splats */
  proximity: number;
  /** From 0 (invisible) to 1 (fully visible) the grid wall in front of the splats */
  gridProximity: number;
  /** Closest splat hit along the ray */
  point: Vector3;
};

/** Information computed while constraining a translation, used to render the guides */
export type GuidanceOutput = {
  /** Whether the last translation was reduced by the corridor boundary or the splats */
  isConstrained: boolean;
  /** Whether the last translation was going towards a visible grid wall, whether it was reduced or not */
  isApproaching: boolean;
  /** Whether any trajectory exists */
  hasBoundary: boolean;
  /** From 0 (far) to 1 (on or beyond) the tunnel boundary */
  boundaryProximity: number;
  /** From 0 (invisible) to 1 (fully visible) the grid wall at the tunnel boundary */
  boundaryGridProximity: number;
  /** Tunnel boundary point closest to the camera */
  boundaryPoint: Vector3;
  /** Outward normal of the tunnel boundary at the boundary point */
  boundaryNormal: Vector3;
  /** Direction of the trajectory segment of the boundary point */
  boundaryTangent: Vector3;
  /** Splats along the view direction */
  splat: SplatGuidance;
  /** Splats above or below the camera, in the direction of its vertical movement */
  verticalSplat: SplatGuidance;
};

/** @returns an empty splat guidance */
function createSplatGuidance(): SplatGuidance {
  return { proximity: 0, gridProximity: 0, point: new Vector3() };
}

/** @returns an empty tunnel point, to be filled by evaluateTunnel */
export function createTunnelPoint(): TunnelPoint {
  return {
    found: false,
    depth: -Infinity,
    boundaryPoint: new Vector3(),
    normal: new Vector3(),
    tangent: new Vector3(),
  };
}

/** @returns an empty guidance output, to be filled by constrainTranslation */
export function createGuidanceOutput(): GuidanceOutput {
  return {
    isConstrained: false,
    isApproaching: false,
    hasBoundary: false,
    boundaryProximity: 0,
    boundaryGridProximity: 0,
    boundaryPoint: new Vector3(),
    boundaryNormal: new Vector3(),
    boundaryTangent: new Vector3(),
    splat: createSplatGuidance(),
    verticalSplat: createSplatGuidance(),
  };
}

/**
 * @returns the segments of the trajectories, with points closer than `spacing` merged together
 * @param polylines the trajectories, at the acquisition device positions
 * @param spacing min distance between two consecutive points
 */
export function buildSegments(
  polylines: Iterable<Vector3[]>,
  spacing: number = P.segmentSpacing,
): Float32Array {
  const values: number[] = [];
  for (const points of polylines) {
    let last = points.at(0);
    for (let i = 1; i < points.length; i++) {
      const p = points[i];
      if (!last || (i < points.length - 1 && last.distanceTo(p) < spacing)) {
        continue;
      }
      values.push(last.x, last.y, last.z, p.x, p.y, p.z);
      last = p;
    }
  }
  return new Float32Array(values);
}

/**
 * @returns the point of a segment at a parameter
 * @param segments the trajectory segments
 * @param o offset of the segment in the array
 * @param t the parameter, from 0 (start) to 1 (end)
 * @param out receives the point
 */
function segmentPoint(
  segments: Float32Array,
  o: number,
  t: number,
  out: Vector3,
): Vector3 {
  return out.set(
    segments[o] + t * (segments[o + 3] - segments[o]),
    segments[o + 1] + t * (segments[o + 4] - segments[o + 1]),
    segments[o + 2] + t * (segments[o + 5] - segments[o + 2]),
  );
}

/**
 * @returns the normalized direction of a segment, the X axis if it is degenerate
 * @param segments the trajectory segments
 * @param o offset of the segment in the array
 * @param out receives the direction
 */
function segmentDirection(
  segments: Float32Array,
  o: number,
  out: Vector3,
): Vector3 {
  out.set(
    segments[o + 3] - segments[o],
    segments[o + 4] - segments[o + 1],
    segments[o + 5] - segments[o + 2],
  );
  const length = out.length();
  return length > LENGTH_EPSILON ? out.divideScalar(length) : out.set(1, 0, 0);
}

/**
 * Computes how deep a position is inside the tunnel of a segment. The closest boundary is either
 * the vertical side, or the top or bottom face, sloped as the segment.
 *
 * @param segments the trajectory segments
 * @param o offset of the segment in the array
 * @param p the position
 * @param out receives the depth and the closest boundary, if given
 * @returns the depth of the position, -Infinity if it is outside of the segment tunnel
 */
function segmentDepth(
  segments: Float32Array,
  o: number,
  p: Readonly<Vector3>,
  out?: TunnelPoint,
): number {
  const R = P.corridorRadius;
  const H = P.tunnelHalfHeight;
  const x0 = segments[o];
  const y0 = segments[o + 1];
  const z0 = segments[o + 2];
  const sx = segments[o + 3] - x0;
  const sy = segments[o + 4] - y0;
  const sz = segments[o + 5] - z0;

  // Range of the segment points within H vertically from the position
  let lo = 0;
  let hi = 1;
  if (Math.abs(sy) > LENGTH_EPSILON) {
    const a = (p.y - H - y0) / sy;
    const b = (p.y + H - y0) / sy;
    lo = Math.max(0, Math.min(a, b));
    hi = Math.min(1, Math.max(a, b));
  } else if (Math.abs(p.y - y0) > H) {
    return -Infinity;
  }
  if (lo > hi) return -Infinity;

  const hLenSq = sx * sx + sz * sz;
  const hLen = Math.sqrt(hLenSq);
  const isVertical = hLen < LENGTH_EPSILON;
  let t = 0;
  if (isVertical) {
    if (Math.abs(sy) > LENGTH_EPSILON) t = (p.y - y0) / sy;
  } else {
    t = ((p.x - x0) * sx + (p.z - z0) * sz) / hLenSq;
  }
  t = clamp(t, lo, hi);
  const hx = p.x - (x0 + t * sx);
  const hz = p.z - (z0 + t * sz);
  const dy = p.y - (y0 + t * sy);
  const dh = Math.hypot(hx, hz);
  const sideDepth = R - dh;
  // Distance to the sloped top or bottom face, along its normal
  const capDepth =
    (H - Math.abs(dy)) * (isVertical ? 1 : hLen / Math.hypot(hLen, sy));
  const depth = Math.min(sideDepth, capDepth);
  if (depth < 0) return -Infinity;

  if (out) {
    const { normal, tangent } = out;
    segmentDirection(segments, o, tangent);
    if (sideDepth <= capDepth) {
      if (dh > LENGTH_EPSILON) normal.set(hx / dh, 0, hz / dh);
      else if (isVertical) normal.set(1, 0, 0);
      else normal.set(-sz / hLen, 0, sx / hLen);
    } else {
      const sign = dy < 0 ? -1 : 1;
      if (isVertical) {
        normal.set(0, sign, 0);
      } else {
        // The vertical axis made orthogonal to the segment
        const ty = tangent.y;
        normal
          .set(-ty * tangent.x, 1 - ty * ty, -ty * tangent.z)
          .normalize()
          .multiplyScalar(sign);
      }
    }
    out.depth = depth;
    out.boundaryPoint.copy(p).addScaledVector(normal, depth);
  }
  return depth;
}

/**
 * @returns the distance of a position from the tunnel cylinder centered at a segment point
 * @param segments the trajectory segments
 * @param o offset of the segment in the array
 * @param t parameter of the segment point
 * @param p the position
 */
function cylinderDistance(
  segments: Float32Array,
  o: number,
  t: number,
  p: Readonly<Vector3>,
): number {
  const c = segmentPoint(segments, o, t, CENTER);
  const dh = Math.max(0, Math.hypot(p.x - c.x, p.z - c.z) - P.corridorRadius);
  const dv = Math.max(0, Math.abs(p.y - c.y) - P.tunnelHalfHeight);
  return Math.hypot(dh, dv);
}

/**
 * @returns the parameter of the segment point whose tunnel cylinder is closest to a position.
 * The distance is convex along the segment, so a golden-section search finds it.
 * @param segments the trajectory segments
 * @param o offset of the segment in the array
 * @param p the position
 */
function closestCylinder(
  segments: Float32Array,
  o: number,
  p: Readonly<Vector3>,
): number {
  let a = 0;
  let b = 1;
  for (let i = 0; i < PROJECTION_ITERATIONS; i++) {
    const c = b - GOLDEN_RATIO * (b - a);
    const d = a + GOLDEN_RATIO * (b - a);
    if (
      cylinderDistance(segments, o, c, p) < cylinderDistance(segments, o, d, p)
    ) {
      b = d;
    } else {
      a = c;
    }
  }
  return (a + b) / 2;
}

/**
 * @returns the distance of a coordinate from the range of a segment along an axis, enlarged by a margin
 * @param segments the trajectory segments
 * @param o offset of the segment in the array
 * @param axis 0 for x, 1 for y, 2 for z
 * @param v the coordinate
 * @param margin the margin
 */
function axisDistance(
  segments: Float32Array,
  o: number,
  axis: number,
  v: number,
  margin: number,
): number {
  const a = segments[o + axis];
  const b = segments[o + axis + 3];
  return Math.max(0, Math.min(a, b) - margin - v, v - Math.max(a, b) - margin);
}

/**
 * @returns a lower bound of the distance of a position from the tunnel of a segment, from its bounding box
 * @param segments the trajectory segments
 * @param o offset of the segment in the array
 * @param p the position
 */
function tunnelDistanceLowerBound(
  segments: Float32Array,
  o: number,
  p: Readonly<Vector3>,
): number {
  return Math.hypot(
    axisDistance(segments, o, 0, p.x, P.corridorRadius),
    axisDistance(segments, o, 1, p.y, P.tunnelHalfHeight),
    axisDistance(segments, o, 2, p.z, P.corridorRadius),
  );
}

/**
 * Finds the tunnel point closest to a position outside of all the tunnels
 *
 * @param segments the trajectory segments, not empty
 * @param p the position
 * @param out receives the closest point
 */
function closestOutside(
  segments: Float32Array,
  p: Readonly<Vector3>,
  out: TunnelPoint,
): void {
  // Starting from the closest bounding box allows to skip most of the other segments
  let best = 0;
  let bound = Infinity;
  for (let o = 0; o < segments.length; o += SEGMENT_STRIDE) {
    const b = tunnelDistanceLowerBound(segments, o, p);
    if (b < bound) {
      bound = b;
      best = o;
    }
  }
  const seed = best;
  let bestT = closestCylinder(segments, seed, p);
  let bestDistance = cylinderDistance(segments, seed, bestT, p);
  for (let o = 0; o < segments.length; o += SEGMENT_STRIDE) {
    if (
      o === seed ||
      tunnelDistanceLowerBound(segments, o, p) >= bestDistance
    ) {
      continue;
    }
    const t = closestCylinder(segments, o, p);
    const d = cylinderDistance(segments, o, t, p);
    if (d < bestDistance) {
      bestDistance = d;
      bestT = t;
      best = o;
    }
  }

  const R = P.corridorRadius;
  const H = P.tunnelHalfHeight;
  const c = segmentPoint(segments, best, bestT, CENTER);
  const hx = p.x - c.x;
  const hz = p.z - c.z;
  const dh = Math.hypot(hx, hz);
  const k = dh > R ? R / dh : 1;
  out.boundaryPoint.set(
    c.x + hx * k,
    c.y + clamp(p.y - c.y, -H, H),
    c.z + hz * k,
  );
  out.normal.subVectors(p, out.boundaryPoint).normalize();
  out.depth = -bestDistance;
  segmentDirection(segments, best, out.tangent);
}

/**
 * Finds the position of a point relative to the trajectory tunnels
 *
 * @param segments the trajectory segments
 * @param position the position, usually the camera
 * @param out receives the tunnel point
 * @returns the `out` parameter
 */
export function evaluateTunnel(
  segments: Float32Array,
  position: Readonly<Vector3>,
  out: TunnelPoint,
): TunnelPoint {
  out.found = segments.length > 0;
  out.depth = -Infinity;
  let best = -1;
  for (let o = 0; o < segments.length; o += SEGMENT_STRIDE) {
    const depth = segmentDepth(segments, o, position);
    if (depth > out.depth) {
      out.depth = depth;
      best = o;
    }
  }
  if (best >= 0) segmentDepth(segments, best, position, out);
  else if (out.found) closestOutside(segments, position, out);
  return out;
}

/**
 * @returns the fraction of the speed to keep, from 1 (no slowdown) to 0 (stopped).
 * The speed drops quickly as soon as the proximity grows, see P.slowdownExponent.
 * @param proximity from 0 (far) to 1 (at the boundary)
 */
export function slowdownFactor(proximity: number): number {
  return (1 - clamp(proximity, 0, 1)) ** P.slowdownExponent;
}

/**
 * Slows down the component of a camera translation going towards the splats hit by a ray,
 * stopping it at P.splatMinDistance from them
 *
 * @param from camera position before the translation
 * @param to camera position after the translation, modified in place
 * @param hit result of the last raycast on the splats
 * @param out receives information on the splats, to render the guides
 * @returns whether the translation goes towards the splats within the grid distance
 */
function slowDownTowardsSplats(
  from: Readonly<Vector3>,
  to: Vector3,
  hit: Readonly<SplatHit>,
  out: SplatGuidance,
): boolean {
  out.proximity = 0;
  out.gridProximity = 0;
  if (!hit.valid) return false;

  const { origin: o, direction: u } = hit;
  // Account for the movement since the raycast was done
  const travelled =
    (from.x - o.x) * u.x + (from.y - o.y) * u.y + (from.z - o.z) * u.z;
  const h = hit.distance - travelled;
  out.proximity = clamp(
    (P.splatWarningDistance - h) /
      (P.splatWarningDistance - P.splatMinDistance),
    0,
    1,
  );
  out.gridProximity = clamp(
    (P.splatGridDistance - h) / (P.splatGridDistance - P.splatMinDistance),
    0,
    1,
  );
  out.point.copy(o).addScaledVector(u, hit.distance);
  const forward =
    (to.x - from.x) * u.x + (to.y - from.y) * u.y + (to.z - from.z) * u.z;
  if (forward > 0) {
    const allowed = Math.max(0, h - P.splatMinDistance);
    const kept = Math.min(forward * slowdownFactor(out.proximity), allowed);
    to.addScaledVector(u, kept - forward);
  }
  return forward > 0 && out.gridProximity > 0;
}

/**
 * Stops the component of a camera translation going towards the tunnel boundary or the splats.
 * The tunnel boundary is never crossed, but a camera already outside of it can come back.
 * Movements along or away from the tunnel boundary are kept, so the camera slides along it,
 * following the slope of the trajectory.
 *
 * @param from camera position before the translation
 * @param to camera position after the translation, modified in place
 * @param segments the trajectory segments
 * @param splatHit result of the last raycast on the splats along the view direction
 * @param verticalSplatHit result of the last raycast on the splats above or below the camera
 * @param tunnel scratch object
 * @param out receives information on the boundaries, to render the guides
 */
export function constrainTranslation(
  from: Readonly<Vector3>,
  to: Vector3,
  segments: Float32Array,
  splatHit: Readonly<SplatHit>,
  verticalSplatHit: Readonly<SplatHit>,
  tunnel: TunnelPoint,
  out: GuidanceOutput,
): void {
  const { x: requestedX, y: requestedY, z: requestedZ } = to;
  const move = MOVE.subVectors(to, from);

  evaluateTunnel(segments, from, tunnel);
  out.hasBoundary = tunnel.found;
  out.boundaryProximity = 0;
  out.boundaryGridProximity = 0;
  // A camera already outside of the tunnels must not go farther from them
  let minDepth = 0;
  let isApproaching = false;
  if (tunnel.found) {
    const { depth, normal } = tunnel;
    minDepth = Math.min(0, depth);
    const W = P.boundaryWarningDistance;
    const G = P.boundaryGridDistance;
    out.boundaryProximity = clamp((W - depth) / W, 0, 1);
    out.boundaryGridProximity = clamp((G - depth) / G, 0, 1);
    out.boundaryPoint.copy(tunnel.boundaryPoint);
    out.boundaryNormal.copy(normal);
    out.boundaryTangent.copy(tunnel.tangent);
    const outward = move.dot(normal);
    isApproaching = outward > 0 && out.boundaryGridProximity > 0;
    if (outward > 0) {
      move.addScaledVector(
        normal,
        -outward * (1 - slowdownFactor(out.boundaryProximity)),
      );
    }
  }

  to.copy(from).add(move);
  if (tunnel.found) {
    // A single step must not overshoot the boundary
    evaluateTunnel(segments, to, tunnel);
    if (tunnel.depth < minDepth) {
      to.copy(tunnel.boundaryPoint).addScaledVector(tunnel.normal, -minDepth);
    }
  }
  const approachingSplat = slowDownTowardsSplats(from, to, splatHit, out.splat);
  const approachingVerticalSplat = slowDownTowardsSplats(
    from,
    to,
    verticalSplatHit,
    out.verticalSplat,
  );
  out.isApproaching =
    isApproaching || approachingSplat || approachingVerticalSplat;
  out.isConstrained =
    (to.x - requestedX) ** 2 +
      (to.y - requestedY) ** 2 +
      (to.z - requestedZ) ** 2 >
    CONSTRAINT_EPSILON_SQ;
}
